import { useState } from 'react'
import { createFileRoute, redirect } from '@tanstack/react-router'
import { queryOptions, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, type ApiError, type components } from '../../api/client'
import { Avatar } from '../../components/avatar'
import { Button } from '../../components/ui/button'
import { Input } from '../../components/ui/input'
import { ConfirmDialog } from '../../components/ui/confirm-dialog'

// DIS-39: Settings › Users (admin): search, role, deactivate/reactivate, last seen.
export const Route = createFileRoute('/_app/settings/users')({
  beforeLoad: ({ context }) => {
    if (context.me.role !== 'admin') throw redirect({ to: '/' })
  },
  component: UsersPage,
})

type AdminUser = components['schemas']['AdminUser']

function usersQueryFor(q: string) {
  return queryOptions({
    queryKey: ['admin', 'users', q],
    queryFn: async () => {
      const { data, response } = await api.GET('/admin/users', { params: { query: q ? { q } : {} } })
      if (!data) throw new Error(`GET /admin/users failed: ${response.status}`)
      return data.users
    },
  })
}

function UsersPage() {
  const { me } = Route.useRouteContext()
  const qc = useQueryClient()
  const [q, setQ] = useState('')
  const users = useQuery(usersQueryFor(q))
  const [error, setError] = useState<string | null>(null)
  const [confirm, setConfirm] = useState<{ user: AdminUser; deactivate: boolean } | null>(null)
  const update = useMutation({
    mutationFn: async ({ id, body }: { id: string; body: { role?: 'admin' | 'member' | 'guest'; deactivated?: boolean } }) => {
      const { data, error: err, response } = await api.PATCH('/admin/users/{id}', { params: { path: { id } }, body })
      if (!data) throw new Error((err as ApiError | undefined)?.message ?? `failed (${response.status})`)
      return data
    },
    onSuccess: () => {
      setError(null)
      void qc.invalidateQueries({ queryKey: ['admin', 'users'] })
      void qc.invalidateQueries({ queryKey: ['users'] })
    },
    onError: (e: Error) => setError(e.message),
  })
  return (
    <div className="mx-auto w-full max-w-3xl space-y-4 p-6">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h1 className="text-lg font-semibold">Users</h1>
          <p className="text-[13px] text-fg-muted">Roles take effect on the next request. Deactivating signs the person out everywhere; their messages stay.</p>
        </div>
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, handle, email" aria-label="Search users" className="w-64" />
      </div>
      {error && (
        <p role="alert" className="text-[13px] text-danger">
          {error}
        </p>
      )}
      <div className="overflow-x-auto rounded-lg border border-border">
        <table className="w-full text-[13px]" data-testid="users-table">
          <thead className="bg-bg-muted text-left text-[11px] uppercase tracking-wide text-fg-subtle">
            <tr>
              <th className="px-3 py-2 font-medium">Person</th>
              <th className="px-3 py-2 font-medium">Email</th>
              <th className="px-3 py-2 font-medium">Role</th>
              <th className="px-3 py-2 font-medium">Last seen</th>
              <th className="px-3 py-2 font-medium" />
            </tr>
          </thead>
          <tbody>
            {users.data?.map((u) => {
              const inactive = !!u.deactivated_at
              const self = u.id === me.id
              return (
                <tr key={u.id} className={`border-t border-border ${inactive ? 'opacity-60' : ''}`} data-testid="user-row" data-handle={u.handle}>
                  <td className="px-3 py-2">
                    <span className="flex items-center gap-2">
                      <Avatar user={u} size={24} />
                      <span className="min-w-0">
                        <span className="block truncate font-medium">{u.display_name}</span>
                        <span className="block truncate text-fg-muted">@{u.handle}</span>
                      </span>
                      {inactive && <span className="ml-1 rounded bg-border px-1 text-[10px] uppercase">deactivated</span>}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-fg-muted">{u.email}</td>
                  <td className="px-3 py-2">
                    <select
                      aria-label={`Role for ${u.display_name}`}
                      value={u.role}
                      disabled={self || inactive || update.isPending}
                      onChange={(e) => update.mutate({ id: u.id, body: { role: e.target.value as 'admin' | 'member' | 'guest' } })}
                      className="h-7 rounded-md border border-border bg-bg px-1.5 text-[12px]"
                    >
                      <option value="admin">Admin</option>
                      <option value="member">Member</option>
                      <option value="guest">Guest</option>
                    </select>
                  </td>
                  <td className="px-3 py-2 text-fg-muted">{u.last_seen_at ? new Date(u.last_seen_at).toLocaleString([], { dateStyle: 'medium', timeStyle: 'short' }) : 'Never'}</td>
                  <td className="px-3 py-2 text-right">
                    {!self && (
                      <Button size="sm" variant="ghost" className={inactive ? '' : 'text-danger'} onClick={() => setConfirm({ user: u, deactivate: !inactive })} aria-label={`${inactive ? 'Reactivate' : 'Deactivate'} ${u.display_name}`}>
                        {inactive ? 'Reactivate' : 'Deactivate'}
                      </Button>
                    )}
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
      <ConfirmDialog
        open={!!confirm}
        onClose={() => setConfirm(null)}
        title={confirm?.deactivate ? `Deactivate ${confirm.user.display_name}?` : `Reactivate ${confirm?.user.display_name}?`}
        description={confirm?.deactivate ? 'They are signed out everywhere and can no longer log in. Their messages stay attributed to them.' : 'They can log in again.'}
        confirmLabel={confirm?.deactivate ? 'Deactivate' : 'Reactivate'}
        danger={!!confirm?.deactivate}
        onConfirm={() => {
          if (confirm) update.mutate({ id: confirm.user.id, body: { deactivated: confirm.deactivate } })
          setConfirm(null)
        }}
      />
    </div>
  )
}
